import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs } from '@ctct/payflow-ui';

import creditCardIcon from './assets/v2-creditcard.svg';
import echeckIcon from './assets/v2-echeck.svg';
import type { SingleBillPaymentMethodsLoadedProps } from './types';
import { CreditCard, ECheck } from '../../components';
import { StripeElementEditPayment } from '../PaymentMethodsWidget/StripeElementEditPayment';

import './single-bill-payment-methods-widget-content.scss';

export const SingleBillPaymentMethodsWidgetContent = ({
  data,
  handlers,
  hooks,
}: SingleBillPaymentMethodsLoadedProps) => {
  const { t } = useTranslation();

  const selectedMethod = hooks.useSelectedMethod();
  const selectedBillingCountry = hooks.useSelectedBillingCountry();
  const tabsLoading = hooks.useTabsLoading();
  const isFullLoading = hooks.useFullLoading();

  const getTag = useCallback(
    (method: typeof selectedMethod) => {
      if (data.isCreditCard(method)) {
        return (
          <div className="payment-label-v2">
            <img src={creditCardIcon} alt={t('payment-methods.cc')} data-qe-id="cc-tab" className="tab-item-img" />
            {t('payment-methods.cc')}
          </div>
        );
      }

      if (data.isEcheck(method)) {
        return (
          <div className="payment-label-v2">
            <img src={echeckIcon} alt={t('payment-methods.echeck')} data-qe-id="echeck-tab" className="tab-item-img" />
            {t('payment-methods.echeck')}
          </div>
        );
      }

      return method;
    },
    [data, t]
  );

  const getContent = useCallback(
    (method: typeof selectedMethod) => {
      if (data.isCreditCard(method)) {
        return <CreditCard data={data} handlers={handlers} hooks={hooks} />;
      }

      if (data.isEcheck(method)) {
        return <ECheck data={data} handlers={handlers} hooks={hooks} />;
      }

      return method;
    },
    [data, handlers, hooks]
  );

  if (data.shouldUseStripeElement() && !selectedBillingCountry) {
    return <div className="payment-methods-widget-container">{t('payment-methods.select-billing-country')}</div>;
  }

  if (data.shouldUseStripeElement()) {
    return (
      <div className="payment-methods-widget-container">
        <StripeElementEditPayment
          key={data.getSetupIntentId() ?? 'single-bill-stripe-element'}
          onMount={handlers.onMountStripeElementDiv}
          isLoading={isFullLoading}
        />
      </div>
    );
  }

  return (
    <div className="payment-methods-widget-container v3">
      <div className="tab-selector-container">
        <Tabs
          disableTabs={tabsLoading || isFullLoading}
          disableContent={isFullLoading}
          variant="button-tabs"
          tabs={data.getPaymentMethods().map((method) => ({
            id: method,
            title: getTag(method),
            content: <>{getContent(method)}</>,
          }))}
          value={selectedMethod}
          onValueChange={(val) => handlers.onPaymentMethodSelect(val)}
        />
      </div>
    </div>
  );
};
